import { FunctionComponent, useEffect, useState } from 'react'
import { Daycare } from '@/types/Daycare'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './transformRegularRequest.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { CalendarMonth, Close, LockOutlined } from '@mui/icons-material'
import { NewOccasionalSlot, OccasionalSlotsPlaces, RegularCancellationRequest } from '@/types/Slots'
import moment, { Moment } from 'moment-timezone'
import TimePicker from '@/components/_custom/Picker/timePicker/timePicker'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import ScheduleOccasionalSlot from '../../occasionalSlot/scheduleOccasionalSlot/scheduleOccasionalSlotDialog'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { useMutation } from '@apollo/client'
import { CREATE_OCCASIONAL_SLOT } from '@/modules/API/slot/occasionalSlotHandler'
import { getDaycareClosing, getDaycareOpening } from '@/modules/helper/daycareHelper'
import { ChildDaycareWithParents, ChildSection, ChildWithParents } from '@/types/Child'
import { Section } from '@/types/Section'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useDialog } from '@/hooks/useDialog/useDialog'

interface TransformRegularRequestProps {
    open: boolean
    handleClose: () => void
    daycare: Daycare
    regularRequests: RegularCancellationRequest[]
    childs: ChildWithParents[]
    confirmAllRegularRequest: () => void
    places: OccasionalSlotsPlaces[]
    isRpeOrLaep: boolean
}

type SlotToTransform = {
    regularSlotId: number
    date: string
    start: string
    end: string
    childId?: number
    childName?: string
}

const TransformRegularRequest: FunctionComponent<TransformRegularRequestProps> = ({
    open,
    daycare,
    regularRequests,
    handleClose,
    childs,
    confirmAllRegularRequest,
    places,
    isRpeOrLaep
}: TransformRegularRequestProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [slotToTransform, setSlotToTransform] = useState<SlotToTransform[]>([])

    useEffect(() => {
        const res: SlotToTransform[] = []
        regularRequests.forEach((regularRequest: RegularCancellationRequest): void => {
            const { start, end, regularSlotStart, regularSlotEnd } = regularRequest
            const startMoment: Moment = moment(start, 'HH:mm:ss')
            const endMoment: Moment = moment(end, 'HH:mm:ss')
            const regularSlotStartMoment: Moment = moment(regularSlotStart, 'HH:mm:ss')
            const regularSlotEndMoment: Moment = moment(regularSlotEnd, 'HH:mm:ss')

            if (
                startMoment.isSame(regularSlotStartMoment, 'minute') &&
                endMoment.isSame(regularSlotEndMoment, 'minute')
            ) {
                res.push({
                    date: regularRequest.date,
                    regularSlotId: regularRequest.id,
                    start: startMoment.format('HH:mm'),
                    end: endMoment.format('HH:mm'),
                    childId: regularRequest.childId,
                    childName: regularRequest.childName || undefined
                })
            }

            if (startMoment.isAfter(regularSlotStartMoment)) {
                res.push({
                    date: regularRequest.date,
                    regularSlotId: regularRequest.id,
                    start: regularSlotStartMoment.format('HH:mm'),
                    end: startMoment.format('HH:mm'),
                    childId: regularRequest.childId,
                    childName: regularRequest.childName || undefined
                })
            }

            if (endMoment.isBefore(regularSlotEndMoment)) {
                res.push({
                    date: regularRequest.date,
                    regularSlotId: regularRequest.id,
                    start: endMoment.format('HH:mm'),
                    end: regularSlotEndMoment.format('HH:mm'),
                    childId: regularRequest.childId,
                    childName: regularRequest.childName || undefined
                })
            }
        })
        setSlotToTransform(res)
    }, [regularRequests])

    function findSectionIdByChildId(childId?: number): string | undefined {
        return (
            childs
                .find((child: ChildWithParents): boolean => child.id === childId)
                ?.daycares?.find((d: ChildDaycareWithParents): boolean => d.daycareId === daycare.id)
                ?.sections.find((s: ChildSection): boolean => s.isPrimary)
                ?.sectionId.toString() || undefined
        )
    }

    const [index, setIndex] = useState<number>(0)

    const [start, setStart] = useState<string>(moment(regularRequests[0]?.start, 'HH:mm:ss').format('HH:mm') ?? '')
    const [end, setEnd] = useState<string>(moment(regularRequests[0]?.end, 'HH:mm:ss').format('HH:mm') ?? '')
    const [date, setDate] = useState<string>(regularRequests[0]?.date)
    const [internalNote, setInternalNote] = useState<string>('')

    const [selectSection, setSelectSection] = useState<string>(findSectionIdByChildId(regularRequests[0].childId) ?? '')

    const selectOptionsSection: SelectOptionsDeprecated = [
        {
            options: daycare.sections
                ? daycare.sections.map((section: Section): SelectOptionDeprecated => {
                      return {
                          name: section.name,
                          value: section.id.toString()
                      }
                  })
                : []
        }
    ]

    const [place, setPlace] = useState<string>(regularRequests[0].placeId ? regularRequests[0].placeId.toString() : '')

    const selectOptionsPlace: SelectOptionsDeprecated = [
        {
            options: [
                ...places.map((place: OccasionalSlotsPlaces): SelectOptionDeprecated => {
                    return {
                        name: place.name.valueOf(),
                        value: place.id.toString()
                    }
                }),
                {
                    name: t('noPlaces'),
                    value: ''
                }
            ]
        }
    ]

    const [scheduleDate, setScheduleDate] = useState<string>('')

    const [isScheduleOccasionalSlotDialogOpen, setScheduleOccasionalSlotDialogOpen] = useState<boolean>(false)

    function handleOpenScheduleOccasionalSlotDialog() {
        setScheduleOccasionalSlotDialogOpen(true)
        openScheduleOccasionalSlotDialog()
    }

    useEffect(() => {
        if (regularRequests && regularRequests.length > 0 && slotToTransform && slotToTransform.length > 0) {
            setStart(slotToTransform[index].start)
            setEnd(slotToTransform[index].end)
            setDate(slotToTransform[index].date)
            setInternalNote(`${t('absenceOf')} ${slotToTransform[index].childName}`)
            setSelectSection(findSectionIdByChildId(slotToTransform[index].childId) ?? '')
        }
    }, [index, regularRequests, slotToTransform])

    const { dialog: ScheduleOccasionalSlotDialog, openDialog: openScheduleOccasionalSlotDialog } = useDialog({
        dialogTitle: t('scheduled'),
        Component: ScheduleOccasionalSlot,
        props: (handleClose) => ({
            handleClose: () => {
                handleClose()
                setScheduleOccasionalSlotDialogOpen(false)
            },
            scheduleDate,
            setScheduleDate,
            createOccasionalSlot: createAndNext,
            maxDate: moment(regularRequests[0].date, 'DD-MM-YYYY').format('DD-MM-YYYY')
        }),
        hideDialogClose: true
    })

    const [occasionalSlotToCreate, setOccasionalSlotToCreate] = useState<NewOccasionalSlot[]>([])

    function createNewOccasionalSlot(): NewOccasionalSlot {
        return {
            date: date,
            start: start,
            end: end,
            childId: undefined,
            sectionId: parseInt(selectSection),
            childrenPriority: [],
            groupPriority: [],
            scheduled: isScheduleOccasionalSlotDialogOpen
                ? scheduleDate === ''
                    ? moment(regularRequests[0].date + ' ' + end, 'DD-MM-YYYY HH:mm').format('DD-MM-YYYY HH:mm')
                    : scheduleDate
                : undefined,
            externalIcon: undefined,
            expiry: undefined,
            internalNote: internalNote,
            externalNote: undefined,
            placeId: place === '' ? undefined : Number(place)
        }
    }

    const [createOccasionalSlot, { loading: loadingCreate }] = useMutation(CREATE_OCCASIONAL_SLOT, {
        variables: {
            daycare_id: daycare.id,
            occasionalSlots: occasionalSlotToCreate
        },
        onCompleted: () => {
            showNotificationSuccess(t('absenceTransformed'))
        },
        onError: () => {
            showNotificationError(t('errorTransformedAbsence'))
        }
    })

    function goNext(): void {
        if (index < slotToTransform.length - 1) {
            setIndex(index + 1)
        } else {
            if (occasionalSlotToCreate.length > 0) {
                createOccasionalSlot().then(() => {
                    handleClose()
                    confirmAllRegularRequest()
                })
            } else {
                handleClose()
                confirmAllRegularRequest()
            }
        }
    }

    useEffect(() => {
        if (occasionalSlotToCreate.length > 0) {
            goNext()
        }
    }, [occasionalSlotToCreate])

    function createAndNext(): void {
        setOccasionalSlotToCreate((prevOccasionalSlotToCreate: NewOccasionalSlot[]) => [
            ...prevOccasionalSlotToCreate,
            createNewOccasionalSlot()
        ])
    }

    const minHour: number = Number(
        moment(
            getDaycareOpening(daycare, (moment(regularRequests[0].date, 'DD-MM-YYYY').day() + 6) % 7),
            'HH:mm'
        ).format('HH')
    )
    const minMinute: number = Number(
        moment(
            getDaycareOpening(daycare, (moment(regularRequests[0].date, 'DD-MM-YYYY').day() + 6) % 7),
            'HH:mm'
        ).format('mm')
    )
    const maxHour: number = Number(
        moment(
            getDaycareClosing(daycare, (moment(regularRequests[0].date, 'DD-MM-YYYY').day() + 6) % 7),
            'HH:mm'
        ).format('HH')
    )
    const maxMinute: number = Number(
        moment(
            getDaycareClosing(daycare, (moment(regularRequests[0].date, 'DD-MM-YYYY').day() + 6) % 7),
            'HH:mm'
        ).format('mm')
    )

    const range: number = daycare.merger ? Number(daycare.bookRange.split('_')[1] || '15') : 15

    return (
        <>
            <Dialog open={open} handleClose={handleClose}>
                <DialogHeader title={t('transformAbsence')} />
                <DialogBody className={styles.DialogBody}>
                    {index <= slotToTransform.length && slotToTransform[index] !== undefined && (
                        <>
                            <div className={styles.Container}>
                                <div className={styles.TitleContainer}>
                                    <CalendarMonth className={styles.Icon} />
                                    <div className={styles.TextContainer}>
                                        <p className={styles.FirstText}>
                                            {format(
                                                parse(slotToTransform[index].date, 'dd-MM-yyyy', new Date()),
                                                'EEEE dd MMMM yyyy',
                                                {
                                                    locale: getLocalFromLanguage()
                                                }
                                            )}
                                        </p>
                                        <p className={styles.SecondText}>
                                            {t('from') +
                                                ' ' +
                                                moment(slotToTransform[index].start, 'HH:mm:ss').format('HH:mm') +
                                                ' ' +
                                                t('to').toLowerCase() +
                                                ' ' +
                                                moment(slotToTransform[index].end, 'HH:mm:ss').format('HH:mm')}
                                        </p>
                                    </div>
                                </div>
                                {slotToTransform.length > 1 && <p> {index + 1 + ' / ' + slotToTransform.length}</p>}
                            </div>
                            <div className={styles.BreakLine}></div>
                            <div className={styles.Input}>
                                <TimePicker
                                    value={start}
                                    setValue={setStart}
                                    className={styles.UserInput}
                                    minHour={minHour}
                                    minMinute={minMinute}
                                    maxHour={maxHour}
                                    maxMinute={maxMinute}
                                    endTime={
                                        end
                                            ? moment(end, 'HH:mm').format('HH:mm')
                                            : moment().hours(maxHour).minutes(maxMinute).seconds(0).format('HH:mm')
                                    }
                                    minuteToDisable={range}
                                />
                                <TimePicker
                                    value={end}
                                    setValue={setEnd}
                                    className={styles.UserInput}
                                    minHour={minHour}
                                    minMinute={minMinute}
                                    maxHour={maxHour}
                                    maxMinute={maxMinute}
                                    startTime={
                                        start
                                            ? moment(start, 'HH:mm').format('HH:mm')
                                            : moment().hours(minHour).minutes(minMinute).seconds(0).format('HH:mm')
                                    }
                                    minuteToDisable={range}
                                />
                            </div>
                            <div className={styles.Input}>
                                <SelectUnique
                                    selectOptions={selectOptionsSection}
                                    selectValue={selectSection}
                                    setSelectValue={setSelectSection}
                                    formClassName={styles.UserInput}
                                />
                            </div>
                            {isRpeOrLaep && (
                                <div className={styles.Input}>
                                    <SelectUnique
                                        selectOptions={selectOptionsPlace}
                                        selectValue={place}
                                        setSelectValue={setPlace}
                                        formClassName={styles.UserInput}
                                        label={t('places')}
                                    />
                                </div>
                            )}
                            {slotToTransform.length > 1 && (
                                <div className={styles.ButtonContainer}>
                                    <ButtonTextIcon
                                        type={'custom'}
                                        customIcon={<Close />}
                                        customText={t('doNotTransform')}
                                        onClick={goNext}
                                        className={styles.ButtonFullWidth}
                                    />
                                </div>
                            )}
                            <div className={styles.ButtonContainer}>
                                <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                                <ButtonTextIcon
                                    type={'custom'}
                                    customIcon={<LockOutlined />}
                                    customText={t('scheduled')}
                                    className={styles.Button}
                                    disabled={loadingCreate || selectSection === '' || start === '' || end === ''}
                                    onClick={handleOpenScheduleOccasionalSlotDialog}
                                />
                                <ButtonTextIcon
                                    type={'add'}
                                    onClick={createAndNext}
                                    className={styles.Button}
                                    disabled={loadingCreate || selectSection === '' || start === '' || end === ''}
                                />
                            </div>
                        </>
                    )}
                </DialogBody>
            </Dialog>
            {ScheduleOccasionalSlotDialog}
        </>
    )
}

export default TransformRegularRequest
